import React from "react";
import Dialog from "@mui/material/Dialog";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme, SxProps, Theme } from "@mui/material/styles";
import { useModalLock } from "../../hooks/useModal";
import { breakpoints } from "./mixins";

// Shared shell for Overlay and CenteredOverlay: a centered Dialog on desktop,
// a bottom SwipeableDrawer below the tablet breakpoint. Owns scroll locking,
// Intercom hiding and the paper styling; the wrappers only supply the layout.

const DRAWER_TOP_GAP = 88;

// Gray scrollbars inside the modal. scrollbar-color is inherited, so it
// overrides the site's purple `html { scrollbar-color }` (homepage.css) for
// everything in the modal; browsers that support it (Chrome 121+, Firefox)
// ignore the ::-webkit-scrollbar rules, which remain for older Safari/Chrome.
const scrollbarSx = {
  scrollbarColor: "#CFD1D1 transparent",
  "& *::-webkit-scrollbar": {
    width: "7px",
  },
  "& *::-webkit-scrollbar-thumb": {
    backgroundColor: "#CFD1D1",
    borderRadius: "5px",
  },
};

// Below this breakpoint modals render as a bottom drawer instead of a dialog.
export function useIsOverlayDrawer() {
  return useMediaQuery(`(max-width: ${breakpoints.tablet - 1}px)`, {
    noSsr: true,
  });
}

// Id of the element that labels the modal; wrappers put it on their title.
export const modalTitleId = (id: string) => `${id}-title`;

interface ResponsiveModalProps {
  open: boolean;
  onClose: () => void;
  id: string;
  /** Dialog width in px. */
  width: number;
  /** Dialog border radius in px. */
  radius: number;
  maxHeight?: number | string;
  dialogPaperSx?: SxProps<Theme>;
  /** Rendered inside the drawer (mobile). */
  drawerContent: React.ReactNode;
  /** Rendered inside the dialog (desktop). */
  dialogContent: React.ReactNode;
}

export function ResponsiveModal({
  open,
  onClose,
  id,
  width,
  radius,
  maxHeight,
  dialogPaperSx,
  drawerContent,
  dialogContent,
}: ResponsiveModalProps) {
  const theme = useTheme();
  const isDrawer = useIsOverlayDrawer();

  // MUI's own body scroll lock is disabled below in favour of the shared lock.
  useModalLock(open, id);

  if (isDrawer) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onOpen={() => {}}
        onClose={() => onClose()}
        id={`${id}-drawer`}
        disableSwipeToOpen
        disableScrollLock
        aria-labelledby={modalTitleId(id)}
        sx={{
          zIndex: theme.zIndex.modal,
          // `&&` raises specificity above the page-wide
          // `.MuiDrawer-root > .MuiPaper-root` rules that legacy modals
          // (PleaseLoginPrompt, MoreInfo, MultipleOrganizers) inject via
          // <Global>, which would otherwise override this drawer's height.
          "&& > .MuiDrawer-paper": {
            maxHeight: `calc(100% - ${DRAWER_TOP_GAP}px)`,
            height: "auto",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            ...scrollbarSx,
          },
        }}
      >
        {drawerContent}
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      id={`${id}-dialog`}
      aria-labelledby={modalTitleId(id)}
      disableScrollLock
      PaperProps={{
        sx: [
          {
            bgcolor: "white",
            maxWidth: "revert",
            width,
            borderRadius: `${radius}px`,
            ...(maxHeight != null && { maxHeight }),
            ...scrollbarSx,
          },
          ...(Array.isArray(dialogPaperSx)
            ? dialogPaperSx
            : dialogPaperSx
            ? [dialogPaperSx]
            : []),
        ],
      }}
    >
      {dialogContent}
    </Dialog>
  );
}
